// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import PasswordField from '../components/PasswordField'
import { describeInvitation, acceptInvitation, InvitationDescribeResponse } from '../api/client'
import { useAuth } from '../auth/AuthContext'

interface Props { onScreenEnter: () => void }

export default function AcceptInvitationPage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  const [params] = useSearchParams()
  const tokenFromUrl = params.get('token') || ''
  const navigate = useNavigate()
  // WI-898: detect & clear an existing session when accepting an invite.
  // Without this, a SaaS admin who got the invite in the same browser
  // accepts the invite, gets redirected to /login, /login sees them as
  // still-authenticated-as-SaaS-admin, and bounces them to / which lands
  // them back on SaaS home — not their new pharmacy account.
  const { me, signOut } = useAuth()

  const [tokenInput, setTokenInput] = useState(tokenFromUrl)
  const [describing, setDescribing] = useState(false)
  const [description, setDescription] = useState<InvitationDescribeResponse | null>(null)

  const [displayName, setDisplayName] = useState('')
  const [pw, setPw]  = useState('')
  const [pw2, setPw2] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // If the URL provided a token, validate it on mount
  useEffect(() => {
    if (!tokenFromUrl) return
    setDescribing(true)
    describeInvitation(tokenFromUrl)
      .then(d => {
        setDescription(d)
        if (d.valid && d.display_name) setDisplayName(d.display_name)
      })
      .catch(() => setDescription({ valid: false, error: 'invalid_token' }))
      .finally(() => setDescribing(false))
  }, [tokenFromUrl])

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault()
    if (!tokenInput.trim()) return
    setDescribing(true)
    setDescription(null)
    setError(null)
    try {
      const d = await describeInvitation(tokenInput.trim())
      setDescription(d)
      if (d.valid && d.display_name) setDisplayName(d.display_name)
    } catch (e: any) {
      setError(e?.body?.message || 'Could not look up invitation.')
    } finally {
      setDescribing(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (pw.length < 12) { setError('Password must be at least 12 characters.'); return }
    if (pw !== pw2)     { setError('Passwords don’t match.'); return }
    if (!description?.valid) { setError('Invitation is not valid.'); return }

    setSubmitting(true)
    try {
      const res = await acceptInvitation({
        token: tokenInput.trim(),
        password: pw,
        display_name: displayName.trim() || undefined,
      })
      // WI-898: if an existing session is active (e.g. the SaaS admin
      // who issued the invite is in the same browser), sign it out
      // before we send the new user to their pharmacy login. Otherwise
      // the login page sees an authenticated session and bounces to /
      // which lands on the OLD user's home — never on the new account.
      // Always clear any residual session (typically the SaaS admin who issued the invite in this
      // same browser) before sending the new user to their pharmacy login. We sign out
      // UNCONDITIONALLY: `me` may not be resolved yet on a freshly-opened email link, so the
      // authenticated check alone can miss a live session cookie and the login page would then
      // bounce to '/'. signOut is a safe no-op when there's nothing to clear.
      try { await signOut() } catch { /* best effort */ }
      setDone(true)
      // Send the new user to THEIR pharmacy's branded login page so they know where to sign in. The
      // branded login route is /p/{url_code}/login — url_code is the opaque code real pharmacy URLs use
      // (the SaaS admin portal's 'Login URL'); prefer it, fall back to slug, then to the generic /login.
      const code = res?.pharmacy_url_code || res?.pharmacy_slug
      const dest = code ? `/p/${code}/login` : '/login'
      setTimeout(() => navigate(dest, { replace: true }), 2500)
    } catch (e: any) {
      const code = e?.body?.error
      const msg =
        code === 'invalid_token'             ? 'This invitation link is no longer valid.' :
        code === 'expired'                   ? 'This invitation has expired. Ask your administrator for a new one.' :
        code === 'already_accepted'          ? 'This invitation has already been accepted. Try signing in instead.' :
        code === 'revoked'                   ? 'This invitation was revoked.' :
        code === 'email_already_registered'  ? 'An account already exists for this email. Try signing in instead.' :
        code === 'weak_password'             ? 'Password must be at least 12 characters.' :
        e?.body?.message || 'Could not accept invitation. Please try again.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div style={containerStyle}>
        <div style={successStyle}>
          <CheckCircle2 size={20} aria-hidden="true" />
          <div>
            <strong>Welcome to OwlRx Portal!</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5 }}>
              Your account is set up.{' '}
              {me?.authenticated
                ? <>Signed you out of the previous session. Redirecting you to sign in as <strong>{description?.email}</strong>…</>
                : <>Redirecting you to sign in…</>}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // No token in URL → show a paste-token form
  if (!tokenFromUrl && !description) {
    return (
      <div style={containerStyle}>
        <h1 style={titleStyle}>Accept your invitation</h1>
        <p style={subtitleStyle}>
          Paste the invitation token from your email below. (You can also click
          the link in the email directly.)
        </p>
        <form onSubmit={handleLookup} style={formStyle} noValidate>
          <label style={labelStyle} htmlFor="token">Invitation token</label>
          <input
            id="token"
            type="text"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            disabled={describing}
            required
            style={inputStyle}
            placeholder="Paste the long string from your email"
          />
          {error && (
            <div role="alert" style={errorStyle}>
              <AlertCircle size={16} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          <button type="submit" disabled={describing} style={submitStyle}>
            {describing ? (<><Loader2 size={16} className="spin" /> Checking…</>) : 'Continue'}
          </button>
        </form>
        <div style={altLinksStyle}>
          <Link to="/login" style={linkStyle}>Already have an account? Sign in</Link>
        </div>
      </div>
    )
  }

  // Token is present but couldn't validate
  if (description && !description.valid) {
    const code = description.error
    const msg =
      code === 'expired'          ? 'This invitation has expired. Ask your administrator for a new one.' :
      code === 'already_accepted' ? 'This invitation was already accepted. Try signing in.' :
      code === 'revoked'          ? 'This invitation was revoked.' :
      'This invitation link is invalid.'
    return (
      <div style={containerStyle}>
        <div style={errorBigStyle}>
          <AlertCircle size={20} aria-hidden="true" />
          <div>
            <strong>{msg}</strong>
          </div>
        </div>
        <div style={altLinksStyle}>
          <Link to="/login" style={linkStyle}>Back to sign in</Link>
        </div>
      </div>
    )
  }

  // Valid invitation → show the set-password form
  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Welcome to OwlRx Portal</h1>
      <p style={subtitleStyle}>
        You've been invited to join.{' '}
        <strong>{description?.email}</strong>{' '}
        is the email associated with this invitation. Set your password below
        to finish creating your account.
      </p>

      {/* WI-898: warn when accepting an invite while already signed in
          as someone else — we'll sign them out after accept, but they
          should know they're switching identities. */}
      {me?.authenticated && me.email !== description?.email && (
        <div style={warnBoxStyle}>
          <AlertCircle size={16} aria-hidden="true" />
          <div>
            You're currently signed in as <strong>{me.email}</strong>.
            When you finish, you'll be signed out and asked to sign in as{' '}
            <strong>{description?.email}</strong>.
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={formStyle} noValidate>
        <label style={labelStyle} htmlFor="dn">Display name</label>
        <input
          id="dn"
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          disabled={submitting}
          style={inputStyle}
          placeholder="How should we show your name?"
        />

        <label style={labelStyle} htmlFor="pw">Password</label>
        <PasswordField
          id="pw"
          autoComplete="new-password"
          value={pw}
          onChange={setPw}
          disabled={submitting}
          minLength={12}
          required
          style={inputStyle}
        />

        <label style={labelStyle} htmlFor="pw2">Confirm password</label>
        <PasswordField
          id="pw2"
          autoComplete="new-password"
          value={pw2}
          onChange={setPw2}
          disabled={submitting}
          minLength={12}
          required
          style={inputStyle}
        />

        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? (<><Loader2 size={16} className="spin" /> Creating account…</>) : 'Create my account'}
        </button>
      </form>
    </div>
  )
}

const containerStyle: React.CSSProperties = { maxWidth: 440, margin: '0 auto', padding: '32px 24px' }
const titleStyle: React.CSSProperties = { fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }
const subtitleStyle: React.CSSProperties = { margin: '0 0 24px', color: 'var(--color-text-soft)', fontSize: 14, lineHeight: 1.5 }
const formStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 }
const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, marginTop: 12 }
const inputStyle: React.CSSProperties = { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', background: 'var(--color-surface)' }
const errorStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 12px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, color: '#991b1b', fontSize: 13 }
const errorBigStyle: React.CSSProperties = { display: 'flex', gap: 12, padding: '14px 16px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 14 }
const successStyle: React.CSSProperties = { display: 'flex', gap: 12, padding: '14px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, color: '#065f46', fontSize: 14 }
const warnBoxStyle: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16, padding: '10px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, color: '#78350f', fontSize: 13, lineHeight: 1.45 }
const submitStyle: React.CSSProperties = { marginTop: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 18px', background: 'var(--color-primary, #047857)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
const altLinksStyle: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 24, fontSize: 13 }
const linkStyle: React.CSSProperties = { color: 'var(--color-primary, #047857)', textDecoration: 'none' }
